<script setup>
import { ref } from 'vue'
defineProps({
  title: String,
  url: String,
  description: String,
})

const copyToastVisible = ref(false)
let copyToastTimer = null

const showCopyToast = () => {
  copyToastVisible.value = true
  clearTimeout(copyToastTimer)
  copyToastTimer = setTimeout(() => { copyToastVisible.value = false }, 2000)
}

const share = (platform) => {
  const u = encodeURIComponent(window.location.href)
  const t = encodeURIComponent(document.title)
  const urls = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    twitter: `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
    line: `https://social-plugins.line.me/lineit/share?url=${u}`,
    copy: null
  }
  if (platform === 'copy') {
    navigator.clipboard.writeText(window.location.href)
    showCopyToast()
    return
  }
  window.open(urls[platform], '_blank', 'width=600,height=400')
}
</script>

<template>
  <div class="flex items-center gap-2 relative">
    <!-- Copy Toast -->
    <div v-if="copyToastVisible" class="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-lg shadow-lg whitespace-nowrap z-50 flex items-center gap-1">
      <span>คัดลอกลิงก์แล้ว</span>
      <svg class="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </div>

    <span class="text-xs font-bold text-gray-600 dark:text-gray-400 mr-1">แชร์</span>
    <!-- Facebook -->
    <button @click="share('facebook')" class="w-8 h-8 rounded-full bg-[#1877F2]/10 hover:bg-[#1877F2] text-[#1877F2] hover:text-white flex items-center justify-center transition-all duration-300 hover:scale-110" title="Facebook" aria-label="แชร์ไปยัง Facebook">
      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
    </button>
    <!-- Line -->
    <button @click="share('line')" class="w-8 h-8 rounded-full bg-[#06C755]/10 hover:bg-[#06C755] text-[#06C755] hover:text-white flex items-center justify-center transition-all duration-300 hover:scale-110" title="Line" aria-label="แชร์ไปยัง Line">
      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M 19.365 9.863 c 0.349 0 0.63 0.285 0.63 0.631 0 0.345 -0.281 0.63 -0.63 0.63 H 17.61 v 1.125 h 1.755 c 0.349 0 0.63 0.283 0.63 0.63 0 0.344 -0.281 0.629 -0.63 0.629 h -2.386 c -0.345 0 -0.627 -0.285 -0.627 -0.629 V 8.108 c 0 -0.345 0.282 -0.63 0.627 -0.63 h 2.386 c 0.346 0 0.627 0.285 0.627 0.63 0 0.349 -0.281 0.63 -0.63 0.63 H 17.61 v 1.125 h 1.755 z m -3.855 3.016 c 0 0.27 -0.174 0.51 -0.432 0.596 -0.064 0.021 -0.133 0.031 -0.199 0.031 -0.211 0 -0.391 -0.09 -0.51 -0.25 l -2.443 -3.317 v 2.94 c 0 0.344 -0.279 0.629 -0.631 0.629 -0.346 0 -0.626 -0.285 -0.626 -0.629 V 8.108 c 0 -0.27 0.173 -0.51 0.43 -0.595 0.06 -0.023 0.136 -0.033 0.194 -0.033 c 0.195 0 0.375 0.104 0.495 0.254 l 2.462 3.33 V 8.108 c 0 -0.345 0.282 -0.63 0.63 -0.63 c 0.345 0 0.63 0.285 0.63 0.63 v 4.771 z m -5.741 0 c 0 0.344 -0.282 0.629 -0.631 0.629 c -0.345 0 -0.627 -0.285 -0.627 -0.629 V 8.108 c 0 -0.345 0.282 -0.63 0.627 -0.63 c 0.346 0 0.628 0.285 0.628 0.63 v 4.771 z m -2.466 0.629 H 4.917 c -0.345 0 -0.63 -0.285 -0.63 -0.629 V 8.108 c 0 -0.345 0.285 -0.63 0.63 -0.63 c 0.348 0 0.63 0.285 0.63 0.63 v 4.141 h 1.756 c 0.348 0 0.629 0.283 0.629 0.63 0 0.344 -0.282 0.629 -0.629 0.629 M 24 10.314 C 24 4.943 18.615 0.572 12 0.572 S 0 4.943 0 10.314 c 0 4.811 4.27 8.842 10.035 9.608 c 0.391 0.082 0.923 0.258 1.058 0.59 c 0.12 0.301 0.079 0.766 0.038 1.08 l -0.164 1.02 c -0.045 0.301 -0.24 1.186 1.049 0.645 c 1.291 -0.539 6.916 -4.078 9.436 -6.975 C 23.176 14.393 24 12.458 24 10.314"/></svg>
    </button>
    <!-- Twitter/X -->
    <button @click="share('twitter')" class="w-8 h-8 rounded-full bg-black/5 dark:bg-white/10 hover:bg-black text-gray-700 dark:text-gray-300 hover:text-white flex items-center justify-center transition-all duration-300 hover:scale-110" title="X (Twitter)" aria-label="แชร์ไปยัง X (Twitter)">
      <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
    </button>
    <!-- Copy Link -->
    <button @click="share('copy')" class="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-emerald-500 text-gray-500 hover:text-white flex items-center justify-center transition-all duration-300 hover:scale-110" title="คัดลอกลิงก์" aria-label="คัดลอกลิงก์สินค้า">
      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path></svg>
    </button>
  </div>
</template>
